import { Alert, Flex, Group, Loader, Space, Table, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import useGetProjetoItem from "@/modules/analisar-resposta/components/questionario-item/useGetProjetoNew";
import { getRespostasMediaEmpresa } from "@/modules/analisar-respostas/firestore";
import { RankingListPaginator } from "@/modules/diagnostico/components/ranking-list/components/RankingListPaginator";
import { usePaginator } from "@/modules/diagnostico/components/ranking-list/hooks/usePaginator";
import useUserStore from "@/modules/users/store";
import ExportExcel from "./ExportExcel";
import ModalFilter from "./ModalFilter";
import GetEmpresas from "../graphics-nivo/useGetEmpresa";
import TableItem from "../table-item";
// prettier-ignore
interface Filtro {
  search: string;
  filtro: string;
  habilitada: boolean;
}

interface Habilitada {
  habilitada: boolean;
}

interface StatusResposta {
  idQuestionario: string;
  idEmpresa: string;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
// prettier-ignore
const TableEmpresa = ({ empresas, questionario, selectedDate }: {
  empresas: TConvite[],
  questionario: string,
  selectedDate?: string | null
}) => {
  const empresaAtivaId = useUserStore((state) => state.activeEmpresaId);
  const [filter, setFilter] = useState<Filtro>(mockFilter)
  const projeto = useUserStore((state) => state.activeProjeto);
  const { data } = useGetProjetoItem({ idProjeto: projeto || "" });

  // Memoização do array único para evitar recalculo a cada render
  const uniqueArray = useMemo(() =>
    empresas.filter((item, index, self) =>
      index === self.findIndex((t) => t.idEmpresa === item.idEmpresa)
    ), [empresas]
  );

  const { data: newEmpresa = [], isLoading: loading, error } = useQuery({
    queryKey: ["relatorio-empresas", projeto, questionario],
    queryFn: async () => {
      const newData = prepareEmpresaData(uniqueArray, empresas);

      if (!newData?.length) return [];

      const newEmpresa = await GetEmpresas(newData);

      const respostasPromises = newEmpresa.map(item =>
        getRespostasMediaEmpresa(empresaAtivaId || "", item.id)
      );

      // eslint-disable-next-line compat/compat -- Promise.all is supported in target environments
      const respostasResults = await Promise.all(respostasPromises);
      const allRespostas = flattenRespostas(respostasResults as QueryResult[]);
      const respostasMap = buildRespostasMap(allRespostas, questionario);

      const minimoResposta = data?.minimoResposta || 0;
      return newEmpresa.map(item => ({
        ...item,
        habilitada: (respostasMap.get(item.id)?.length || 0) >= minimoResposta
      }));
    },
    enabled: Boolean(projeto && questionario && data),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  });

  // Memoização dos dados filtrados
  const dados = useMemo(() =>
    filteredData(newEmpresa, filter, selectedDate),
    [newEmpresa, filter, selectedDate]
  );

  const { currentPageContent, currentPage, pageSize, setCurrentPage, setPageSize } = usePaginator(dados);

  const rows = useMemo(() =>
    currentPageContent.map((item) => (
      <TableItem key={item.id} data={item} />
    )), [currentPageContent]
  );

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size="1rem" />} title="Erro ao carregar dados" color="red">
        Não foi possível carregar os dados das empresas. Por favor, tente novamente.
      </Alert>
    );
  }

  return (
    <>
      <Group justify={"space-between"} mb={10}>
        <Text size={"md"} fw={700}>Detalhamento das Empresas</Text>
        <Group>
          <ModalFilter setFilter={setFilter} />
          <ExportExcel newEmpresa={dados} />
        </Group>
      </Group>
      <div style={{ overflowX: "auto" }}>

        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Empresa</Table.Th>
              <Table.Th>Código CNAE</Table.Th>
              <Table.Th>Cnpj</Table.Th>
              <Table.Th>Responsável</Table.Th>
              <Table.Th>Telefone</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Porte</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th>Categoria</Table.Th>
              <Table.Th>Data Cadastro</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{loading ? <></> : rows}</Table.Tbody>
        </Table>
        <Flex p="sm" justify="flex-end" gap="sm">
          <RankingListPaginator
            numberOfItems={dados.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </Flex>
      </div>
      <Space h={"md"} />
      {loading ? <Group justify={"center"} align={"center"}>
        <Loader color="blue" />
      </Group> : <></>}
    </>
  )
};

export default TableEmpresa;

/**
 * Prepara os dados das empresas para processamento
 * @param uniqueArray - Array único de empresas
 * @param empresas - Array original de empresas
 * @returns Array combinado e preparado para processamento
 */
const prepareEmpresaData = (uniqueArray: TConvite[], empresas: TConvite[]) => {
  return [...uniqueArray.map((item) => ({ ...item, idEmpresaColaboradorConvidado: item.idEmpresa })), ...empresas];
};

/**
 * Flattens the response results from multiple queries
 * @param respostasResults - Array of query results
 * @returns Flattened array of responses
 */
interface QueryResult {
  docs: Array<{ data: () => TResposta & StatusResposta }>;
}

const flattenRespostas = (respostasResults: QueryResult[]): (TResposta & StatusResposta)[] => {
  return respostasResults.flatMap((result) => result.docs.map((document_) => document_.data()));
};

/**
 * Builds a map of responses grouped by empresa ID
 * @param allRespostas - All responses
 * @param questionario - Questionnaire ID to filter by
 * @returns Map of empresa ID to responses
 */
const buildRespostasMap = (allRespostas: (TResposta & StatusResposta)[], questionario: string) => {
  const respostasMap = new Map<string, (TResposta & StatusResposta)[]>();

  for (const resposta of allRespostas) {
    if (!respostasMap.has(resposta.idEmpresa)) {
      respostasMap.set(resposta.idEmpresa, []);
    }

    if (!resposta.pausado && resposta.idQuestionario === questionario) {
      const empresaRespostas = respostasMap.get(resposta.idEmpresa);
      if (empresaRespostas) {
        empresaRespostas.push(resposta);
      }
    }
  }

  return respostasMap;
};

// prettier-ignore
const filteredData = (data: (TEmpresa & Habilitada)[], filter: Filtro, selectedDate?: string | null) => {
  let filteredByDateData = data;

  // Se uma data foi selecionada no gráfico, filtra por essa data
  if (selectedDate) {
    const PAD_NUMBER = 2;
    filteredByDateData = data.filter(item => {
      const itemDate = new Date(item.createdAt);
      const formattedDate = `${String(itemDate.getDate()).padStart(PAD_NUMBER, '0')}/${String(itemDate.getMonth() + 1).padStart(PAD_NUMBER, '0')}/${itemDate.getFullYear()}`;
      return formattedDate === selectedDate;
    });
  }

  return filteredByDateData.filter(item => doFilter(item, filter))
}

// prettier-ignore
const doFilter = (item: (TEmpresa & Habilitada), filter: Filtro) => {
  switch (filter.filtro) {
    case "Empresa": {
      return filterByEmpresa(item, filter);
    }
    case "Cnpj": {
      return filterByCnpj(item, filter);
    }
    case "Responsavel": {
      return filterByResponsavel(item, filter);
    }
    case "Telefone": {
      return filterByTelefone(item, filter);
    }
    case "Porte": {
      return filterByPorte(item, filter);
    }
    case "Estado": {
      return filterByEstado(item, filter);
    }
    case "Categoria": {
      return filterByCategoria(item, filter);
    }
    case "": {
      return filterByHabilitada(item, filter);
    }
    default: {
      return item;
    }
  }
}
// prettier-ignore
const filterByEmpresa = (item: (TEmpresa & Habilitada), filter: Filtro) => {
  return filter.habilitada ?
    item.nomeFantasia === filter.search && item.habilitada === filter.habilitada :
    item.nomeFantasia === filter.search;
};
// prettier-ignore
const filterByCnpj = (item: (TEmpresa & Habilitada), filter: Filtro) => {
  return filter.habilitada ?
    item.cnpj === filter.search && item.habilitada === filter.habilitada :
    item.cnpj === filter.search;
};
// prettier-ignore
const filterByResponsavel = (item: (TEmpresa & Habilitada), filter: Filtro) => {
  return filter.habilitada ?
    item.responsavel.nome === filter.search && item.habilitada === filter.habilitada :
    item.responsavel.nome === filter.search;
};
// prettier-ignore
const filterByTelefone = (item: (TEmpresa & Habilitada), filter: Filtro) => {
  return filter.habilitada ?
    item.telefone === filter.search && item.habilitada === filter.habilitada :
    item.telefone === filter.search;
};
// prettier-ignore
const filterByPorte = (item: (TEmpresa & Habilitada), filter: Filtro) => {
  return filter.habilitada ?
    item.porte === filter.search && item.habilitada === filter.habilitada :
    item.porte === filter.search;
};
// prettier-ignore
const filterByEstado = (item: (TEmpresa & Habilitada), filter: Filtro) => {
  return filter.habilitada ?
    item.endereco.estado === filter.search && item.habilitada === filter.habilitada :
    item.endereco.estado === filter.search;
};
// prettier-ignore
const filterByCategoria = (item: (TEmpresa & Habilitada), filter: Filtro) => {
  return filter.habilitada ?
    item.categorias === filter.search && item.habilitada === filter.habilitada :
    item.categorias === filter.search;
};
// prettier-ignore
const filterByHabilitada = (item: TEmpresa & Habilitada, filter: Filtro) => {
  return filter.habilitada ? filter.habilitada === item.habilitada : item
}

// prettier-ignore
const mockFilter = {
  search: "",
  filtro: "",
  habilitada: false
};
